<template>
  <nav class="bg-white shadow-sm border-b">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <div class="flex">
          <!-- Logo -->
          <div class="flex-shrink-0 flex items-center">
            <router-link to="/" class="text-xl font-bold text-gray-900">
              {{ t('nav.brand') }}
            </router-link>
          </div>

          <!-- Navigation Links -->
          <div class="hidden sm:ml-6 sm:flex sm:space-x-8">
            <router-link
              to="/"
              class="nav-link"
              :class="{ 'nav-link-active': $route.path === '/' }"
            >
              {{ t('nav.dashboard') }}
            </router-link>
            <router-link
              to="/sales"
              class="nav-link"
              :class="{ 'nav-link-active': $route.path.startsWith('/sales') }"
            >
              {{ t('nav.sales') }}
            </router-link>
            <router-link
              to="/products"
              class="nav-link"
              :class="{ 'nav-link-active': $route.path.startsWith('/products') }"
            >
              {{ t('nav.products') }}
            </router-link>
            <router-link
              to="/warehouses"
              class="nav-link"
              :class="{ 'nav-link-active': $route.path.startsWith('/warehouses') }"
            >
              {{ t('nav.warehouses') }}
            </router-link>
            <router-link
              to="/inventory"
              class="nav-link"
              :class="{ 'nav-link-active': $route.path.startsWith('/inventory') }"
            >
              {{ t('nav.inventory') }}
            </router-link>
          </div>
        </div>

        <!-- Right side -->
        <div class="flex items-center space-x-4">
          <!-- Offline indicator -->
          <div v-if="!isOnline" class="flex items-center text-red-600">
            <span class="text-sm">{{ t('common.offline') }}</span>
          </div>

          <!-- Sync status -->
          <div v-if="hasQueuedActions" class="flex items-center text-yellow-600">
            <span class="text-sm">{{ queuedActionsCount }} {{ t('nav.pending', queuedActionsCount) }}</span>
          </div>

          <!-- Language switcher -->
          <div class="flex items-center rounded-lg bg-gray-100 p-0.5 text-xs font-medium">
            <button
              @click="switchLocale('es')"
              :class="[
                'px-2 py-1 rounded-md transition-colors',
                locale === 'es' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              ]"
            >
              ES
            </button>
            <button
              @click="switchLocale('en')"
              :class="[
                'px-2 py-1 rounded-md transition-colors',
                locale === 'en' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              ]"
            >
              EN
            </button>
          </div>

          <!-- User menu -->
          <div class="relative">
            <button
              @click="toggleUserMenu"
              class="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <span class="sr-only">{{ t('nav.openUserMenu') }}</span>
              <div class="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                <span class="text-sm font-medium text-gray-700">
                  {{ "userInitials" }}
                </span>
              </div>
            </button>

            <!-- Dropdown menu -->
            <div
              v-if="showUserMenu"
              class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div class="py-1">
                <div class="px-4 py-2 text-sm text-gray-700 border-b">
                  {{ authStore.currentUser?.username ?? t('nav.user') }}
                </div>
                <router-link
                  to="/account"
                  @click="showUserMenu = false"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {{ t('nav.changePassword') }}
                </router-link>
                <button
                  @click="handleLogout"
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  {{ t('nav.signOut') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <div v-if="showMobileMenu" class="sm:hidden">
      <div class="pt-2 pb-3 space-y-1">
        <router-link
          to="/"
          class="mobile-nav-link"
          :class="{ 'mobile-nav-link-active': $route.path === '/' }"
        >
          {{ t('nav.dashboard') }}
        </router-link>
        <router-link
          to="/sales"
          class="mobile-nav-link"
          :class="{ 'mobile-nav-link-active': $route.path.startsWith('/sales') }"
        >
          {{ t('nav.sales') }}
        </router-link>
        <router-link
          to="/products"
          class="mobile-nav-link"
          :class="{ 'mobile-nav-link-active': $route.path.startsWith('/products') }"
        >
          {{ t('nav.products') }}
        </router-link>
        <router-link
          to="/warehouses"
          class="mobile-nav-link"
          :class="{ 'mobile-nav-link-active': $route.path.startsWith('/warehouses') }"
        >
          {{ t('nav.warehouses') }}
        </router-link>
        <router-link
          to="/inventory"
          class="mobile-nav-link"
          :class="{ 'mobile-nav-link-active': $route.path.startsWith('/inventory') }"
        >
          {{ t('nav.inventory') }}
        </router-link>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { setLocale } from '../i18n';
import { useOfflineStore } from '../stores/useOfflineStore';
import { useAuthStore } from '../stores/useAuthStore';

const { t, locale } = useI18n();
const router = useRouter();
const offlineStore = useOfflineStore();
const authStore = useAuthStore();

// State
const showUserMenu = ref(false);
const showMobileMenu = ref(false);

// Computed
const isOnline = computed(() => offlineStore.isOnline);
const hasQueuedActions = computed(() => offlineStore.hasQueuedActions);
const queuedActionsCount = computed(() => offlineStore.queuedActionsCount);

// Methods
const switchLocale = (lang: 'en' | 'es') => {
  setLocale(lang);
};

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value;
};

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value;
};

const handleLogout = async () => {
  await authStore.logout();
  showUserMenu.value = false;
  router.push('/login');
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.relative')) {
    showUserMenu.value = false;
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.nav-link {
  @apply inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300;
}

.nav-link-active {
  @apply border-indigo-500 text-gray-900;
}

.mobile-nav-link {
  @apply block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 hover:border-gray-300;
}

.mobile-nav-link-active {
  @apply border-indigo-500 text-indigo-700 bg-indigo-50;
}
</style>
